import type { ProjectOverview } from '../types/project';
import {
  VERDICT_ACCENT_TEXT,
  VERDICT_LABEL,
  VERDICT_PILL,
  VERDICT_RAIL,
} from './project-styles';

interface ProjectsOverviewGridProps {
  projects: ProjectOverview[];
  /** Zoom into a single project's full overview. */
  onOpen: (projectId: string) => void;
}

/** Slim progress read - rose tint when the project is off its deadline. */
function ProgressBar({ pct, isOnTrack }: { pct: number; isOnTrack: boolean }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
      <div
        className={`h-full rounded-full ${
          isOnTrack ? 'bg-emerald-500' : 'bg-rose-500'
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** One mini-stat in the card footer. */
function CardStat({
  value,
  label,
  isUrgent,
}: {
  value: number;
  label: string;
  isUrgent: boolean;
}) {
  return (
    <div className="flex flex-col">
      <span
        className={`text-xl font-bold tracking-tight ${
          isUrgent && value > 0 ? 'text-rose-600' : 'text-zinc-900'
        }`}
      >
        {value}
      </span>
      <span className="text-[11px] font-medium leading-tight text-zinc-400">
        {label}
      </span>
    </div>
  );
}

/** A single project summary card - the whole surface zooms into the deep dive. */
function ProjectSummaryCard({
  project,
  onOpen,
}: {
  project: ProjectOverview;
  onOpen: (projectId: string) => void;
}) {
  const isOnTrack = project.verdict === 'on-track';
  const offBookCount = project.procedures.filter(
    (procedure) => !procedure.isFollowed
  ).length;

  return (
    <button
      type="button"
      onClick={() => onOpen(project.id)}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-zinc-100 bg-white p-6 text-start shadow-[0_24px_60px_-44px_rgba(16,27,58,0.5)] transition-all hover:-translate-y-0.5 hover:shadow-[0_30px_70px_-40px_rgba(16,27,58,0.5)]"
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-5 start-0 w-1 rounded-full ${
          VERDICT_RAIL[project.verdict]
        }`}
      />

      <div className="ps-3">
        {/* Identity + verdict */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
              {project.portfolio}
            </p>
            <h3 className="mt-1 truncate text-lg font-bold tracking-tight text-zinc-900">
              {project.name}
            </h3>
            <p className="mt-0.5 truncate text-sm text-zinc-500">
              {project.team}
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold ${
              VERDICT_PILL[project.verdict]
            }`}
          >
            {VERDICT_LABEL[project.verdict]}
          </span>
        </div>

        {/* Bottom line */}
        <p className="mt-4 line-clamp-2 text-sm leading-relaxed text-zinc-600">
          {project.headline}
        </p>

        {/* Dates + slip */}
        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
          <span className="text-zinc-500">
            Target{' '}
            <span className="font-semibold text-zinc-800">
              {project.targetDate}
            </span>
          </span>
          <span
            className={`font-semibold ${VERDICT_ACCENT_TEXT[project.verdict]}`}
          >
            {isOnTrack
              ? 'On time'
              : `${project.projectedDate} · ${project.slipLabel}`}
          </span>
        </div>

        {/* Progress */}
        <div className="mt-3 flex items-center gap-3">
          <ProgressBar pct={project.progressPct} isOnTrack={isOnTrack} />
          <span className="shrink-0 text-xs font-semibold text-zinc-500">
            {project.progressPct}%
          </span>
        </div>

        {/* Footer stats */}
        <div className="mt-5 flex items-center gap-8 border-t border-zinc-100 pt-4">
          <CardStat
            value={project.breakingCommitments.length}
            label="breaking now"
            isUrgent
          />
          <CardStat value={offBookCount} label="procedures off-book" isUrgent />
          <span className="ms-auto flex items-center gap-1 self-center text-sm font-semibold text-[#0089bd] opacity-0 transition-opacity group-hover:opacity-100">
            Open
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4 rtl:rotate-180"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </div>
      </div>
    </button>
  );
}

/**
 * The portfolio at a glance - every project as a card, each zooming into its
 * full overview. The default view of the Projects surface: see the whole
 * portfolio's health first, then dive into the one that needs you.
 */
export function ProjectsOverviewGrid({
  projects,
  onOpen,
}: ProjectsOverviewGridProps) {
  const atRiskCount = projects.filter(
    (project) => project.verdict !== 'on-track'
  ).length;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">
            Projects
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {projects.length} active ·{' '}
            <span
              className={
                atRiskCount > 0
                  ? 'font-semibold text-rose-600'
                  : 'text-zinc-500'
              }
            >
              {atRiskCount} need attention
            </span>
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {projects.map((project) => (
          <ProjectSummaryCard
            key={project.id}
            project={project}
            onOpen={onOpen}
          />
        ))}
      </div>
    </div>
  );
}
